import { z } from 'zod'

/**
 * Account details for creating a new account during domain import.
 * Used when the domain should be assigned to a new customer account.
 */
export const ImportAccountDetails = z.object({
  email: z.string().email(),
  name: z.string().optional(),
  surname: z.string().optional(),
  org: z.string().optional(),
  line1: z.string().min(1),
  line2: z.string().optional(),
  city: z.string().min(1),
  postcode: z.string().min(1),
  country_iso: z.string().max(2).min(2),
  state: z.string().optional(),
  phone: z.string().min(1),
  ust_id: z.string().optional()
})

export type ImportAccountDetails = z.infer<typeof ImportAccountDetails>

/**
 * Request schema for importing a single domain from INWX.
 * Either account_id (existing) or account (new) should be provided.
 */
export const ImportDomainRequest = z.object({
  domain: z.string().min(1),
  account_id: z.number().int().optional(),
  account: ImportAccountDetails.optional(),
  stripe_customer_id: z.string().optional(),
  parent_account_id: z.number().int().optional(),
  relationship_type: z.enum(['team', 'reseller']).optional(),
  billing_mode: z.enum(['shared', 'independent']).optional()
})

export type ImportDomainRequest = z.infer<typeof ImportDomainRequest>

/**
 * Stripe customer returned from search endpoint.
 * Used for linking existing Stripe customers during domain import.
 */
export const StripeCustomer = z.object({
  id: z.string(),
  email: z.string().email().optional(),
  name: z.string().optional(),
  created: z.number().int()
})

export type StripeCustomer = z.infer<typeof StripeCustomer>

/**
 * Request schema for batch importing multiple domains from INWX.
 */
export const ImportDomainsRequest = z.object({
  imports: z.array(ImportDomainRequest)
})

export type ImportDomainsRequest = z.infer<typeof ImportDomainsRequest>

/**
 * Result of a successful domain import operation.
 */
export const ImportResult = z.object({
  domain_id: z.number().int(),
  subscription_id: z.number().int(),
  account_id: z.number().int(),
  stripe_subscription_id: z.string(),
  active_until: z.string(),
  created_account: z.boolean(),
  parent_account_id: z.number().int().optional(),
  relationship_id: z.number().int().optional()
})

export type ImportResult = z.infer<typeof ImportResult>

/**
 * Individual result entry in a batch import response.
 */
export const ImportResultEntry = z.object({
  domain: z.string(),
  success: z.boolean(),
  error: z.string().optional(),
  result: ImportResult.optional()
})

export type ImportResultEntry = z.infer<typeof ImportResultEntry>

/**
 * Summary statistics for a batch import operation.
 */
export const ImportSummary = z.object({
  total: z.number().int(),
  successful: z.number().int(),
  failed: z.number().int()
})

export type ImportSummary = z.infer<typeof ImportSummary>

/**
 * Response schema for batch domain import operation.
 */
export const ImportDomainsResponse = z.object({
  results: z.array(ImportResultEntry),
  summary: ImportSummary
})

export type ImportDomainsResponse = z.infer<typeof ImportDomainsResponse>

/**
 * Validation result for a domain import check.
 * Also used as the response type for listing available INWX domains.
 */
export const ValidationResult = z.object({
  domain: z.string(),
  exists_in_inwx: z.boolean(),
  exists_in_db: z.boolean(),
  inwx_ro_id: z.number().int().optional(),
  inwx_expiry_date: z.string().optional(),
  can_import: z.boolean(),
  reason: z.string().optional()
})

export type ValidationResult = z.infer<typeof ValidationResult>

/**
 * Request schema for validating a domain import.
 */
export const ValidateImportRequest = z.object({
  domain: z.string().min(1)
})

export type ValidateImportRequest = z.infer<typeof ValidateImportRequest>
